export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID

// Helper genérico — proxy do gtag global. Silencioso quando GA4 ainda não carregou.
const gtag = (...args: unknown[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args)
  }
}

/** Espelha evento no Postgres (telemetria first-party) sem bloquear GA4. */
function mirrorProduct(
  event: string,
  category: string,
  properties?: Record<string, unknown>,
  path?: string,
) {
  if (typeof window === 'undefined') return
  void import('@/components/telemetry/product-telemetry').then(({ trackClientEvent }) => {
    trackClientEvent(event, category, { properties, path })
  })
}

// ===========================================
// Tipos
// ===========================================

export type CalculatorType =
  | 'margem_lucro'
  | 'preco_hora'
  | 'precificacao'
  | 'faturamento'
  | 'fluxo_caixa'
  | 'calendario_das'
  | 'transicao_mei_me'
  | 'ponto_equilibrio'
  | 'quanto_sobra'
  | 'custo_funcionario'
  | 'comparador_tributario'
  | 'roi'

export type PremiumCtaLocal =
  | 'home'
  | 'home_trunfo'
  | 'hero_console'
  | 'pos_calculo'
  | 'premium_hero'
  | 'premium_bottom'
  | 'dashboard'
  | 'configuracoes'
  | 'header'
  | 'footer'
  | 'comprovante_avulso'
  | 'comprovante_generator'

export type TemplateSlug =
  | 'calendario-das-2026'
  | 'tabela-das-2026'
  | 'controle-faturamento-mensal'
  | 'checklist-abertura-mei'

// ===========================================
// Calculadoras
// ===========================================

export const trackCalculadoraAberta = (tipo: CalculatorType) => {
  gtag('event', 'calculadora_aberta', { tipo_calculadora: tipo })
  mirrorProduct('calculator_open', 'calculator', { tipo })
}

export const trackCalculadoraConcluida = (tipo: CalculatorType) => {
  gtag('event', 'calculadora_concluida', { tipo_calculadora: tipo })
  mirrorProduct('calculator_complete', 'calculator', { tipo })
}

// Seleção de calculadora na bancada da home (qual calculadora + de onde)
export const trackCalculadoraSelecionada = (
  tipo: CalculatorType,
  origem: 'home_workspace' | 'pagina_dedicada' = 'home_workspace'
) => {
  gtag('event', 'calculadora_selecionada', { tipo_calculadora: tipo, origem })
}

export const trackCalculadoraSalva = (tipo: CalculatorType) => {
  gtag('event', 'calculadora_salva', { tipo_calculadora: tipo })
  mirrorProduct('calculator_save', 'calculator', { tipo })
}

export const trackCalculadoraPdfBaixado = (tipo: CalculatorType) => {
  gtag('event', 'calculadora_pdf_baixado', { tipo_calculadora: tipo })
  mirrorProduct('calculator_export_pdf', 'calculator', { tipo })
}

export const trackCalculadoraImagemBaixada = (tipo: CalculatorType) => {
  gtag('event', 'calculadora_imagem_baixada', { tipo_calculadora: tipo })
}

export const trackCalculadoraCompartilhada = (tipo: CalculatorType, canal: 'nativo' | 'fallback' = 'nativo') => {
  gtag('event', 'calculadora_compartilhada', { tipo_calculadora: tipo, canal })
}

// ===========================================
// Faturamento (registro mensal — core retention)
// ===========================================

export const trackFaturamentoRegistrado = (mes: number, ano: number, valor: number) => {
  gtag('event', 'faturamento_registrado', { mes, ano, valor })
}

export const trackFaturamentoAtualizado = (mes: number, ano: number, valor: number) => {
  gtag('event', 'faturamento_atualizado', { mes, ano, valor })
}

export const trackFaturamentoApagado = (mes: number, ano: number) => {
  gtag('event', 'faturamento_apagado', { mes, ano })
}

// ===========================================
// Lançamentos do mês vigente (copiloto diário — entradas/saídas)
// ===========================================

export const trackEntradaLancada = (mes: number, ano: number, valor: number) => {
  gtag('event', 'entrada_lancada', { mes, ano, valor })
}

export const trackSaidaLancada = (mes: number, ano: number, valor: number) => {
  gtag('event', 'saida_lancada', { mes, ano, valor })
}

// ===========================================
// Degustação / paywall do painel (gargalo free -> premium)
// ===========================================

/** Painel travou: usuário bateu o limite de lançamentos grátis do mês (HTTP 402). */
export const trackLimiteDegustacaoAtingido = (tipo: 'receita' | 'despesa') => {
  gtag('event', 'limite_degustacao_atingido', { tipo_lancamento: tipo })
}

/** CTA de upgrade do paywall foi exibido para o usuário no painel. */
export const trackPaywallPremiumExibido = (local: 'mes_vigente' = 'mes_vigente') => {
  gtag('event', 'paywall_premium_exibido', { local })
}

// ===========================================
// Limite MEI (gatilho psicológico — quando passa 80% / 100%)
// ===========================================

export const trackLimiteMeiAlertaExibido = (percentual: number) => {
  gtag('event', 'limite_mei_alerta_exibido', { percentual })
}

export const trackLimiteMeiExcedido = (faturamentoTotal: number) => {
  gtag('event', 'limite_mei_excedido', { faturamento_total: faturamentoTotal })
}

// ===========================================
// Retenção e hábito (logado)
// ===========================================

export const trackStreakAtingido = (meses: 1 | 3 | 6 | 12) => {
  gtag('event', 'streak_atingido', { meses })
}

export const trackNotificacaoPushAtivada = () => {
  gtag('event', 'notificacao_push_ativada')
}

export const trackNotificacaoPushDesativada = () => {
  gtag('event', 'notificacao_push_desativada')
}

export const trackAlertaEmailAlterado = (tipo: 'das', ativado: boolean) => {
  gtag('event', 'alerta_email_alterado', { tipo, ativado })
}

export const trackPerfilEditado = () => {
  gtag('event', 'perfil_editado')
}

export const trackDadosExportados = () => {
  gtag('event', 'dados_exportados')
}

export const trackContaDeletada = () => {
  gtag('event', 'conta_deletada')
}

export const trackLogoutRealizado = () => {
  gtag('event', 'logout_realizado')
}

// ===========================================
// Tutorial / Onboarding
// ===========================================

export const trackTutorialIniciado = () => {
  gtag('event', 'tutorial_iniciado')
}

export const trackTutorialPassoConcluido = (passo: number, nomePasso: string) => {
  gtag('event', 'tutorial_passo_concluido', { numero_passo: passo, nome_passo: nomePasso })
}

export const trackTutorialConcluido = () => {
  gtag('event', 'tutorial_concluido')
}

export const trackTutorialPulado = (noPasso: number) => {
  gtag('event', 'tutorial_pulado', { pulado_no_passo: noPasso })
}

// ===========================================
// Painel (dashboard)
// ===========================================

export const trackPainelAberto = () => {
  gtag('event', 'painel_aberto')
  gtag('event', 'dashboard_visto')
  mirrorProduct('panel_open', 'panel', undefined, '/dashboard')
}

export const trackLeadCapturado = (source: string, alertType: string) => {
  gtag('event', 'lead_capturado', { source, alert_type: alertType })
  mirrorProduct('lead_captured', 'growth', { source, alertType })
}

/** Checklist do perdão das dívidas (LC 238/2026) — cada resposta dada. */
export const trackPerdaoChecklistRespondido = (pergunta: string, resposta: string) => {
  gtag('event', 'perdao_checklist_respondido', { pergunta, resposta })
  mirrorProduct('perdao_checklist_answered', 'growth', { pergunta, resposta })
}

export const trackLembreteMensalExibido = () => {
  gtag('event', 'lembrete_mensal_exibido')
}

export const trackLembreteMensalClicado = () => {
  gtag('event', 'lembrete_mensal_clicado')
}

export const trackLembreteMensalFechado = () => {
  gtag('event', 'lembrete_mensal_fechado')
}

export const trackSugestaoContextualClicada = (deCalculadora: string, paraCalculadora: string) => {
  gtag('event', 'sugestao_contextual_clicada', {
    de_calculadora: deCalculadora,
    para_calculadora: paraCalculadora,
  })
}

// ===========================================
// Hero (landing page topo)
// ===========================================

export const trackHeroCalcUsada = (aba: string) => {
  gtag('event', 'hero_calc_usada', { aba })
}

export const trackHeroCalcConcluida = (aba: string) => {
  gtag('event', 'hero_calc_concluida', { aba })
  mirrorProduct('calculator_complete', 'calculator', { origem: 'hero', aba }, '/')
}

export const trackHeroCtaClicado = (destino: string, local: string) => {
  gtag('event', 'hero_cta_clicado', { destino, local })
}

export const trackHomeCardClicado = (calculadora: string, posicao?: number) => {
  gtag('event', 'home_card_clicado', { calculadora, posicao })
  mirrorProduct('home_calc_card_click', 'calculator', { slug: calculadora, posicao }, '/')
}

// ===========================================
// Conversão Premium
// ===========================================

export const trackPremiumCtaClicado = (local: PremiumCtaLocal) => {
  gtag('event', 'premium_cta_clicado', { local })
  mirrorProduct('premium_cta_click', 'billing', { local })
}

export const trackCheckoutIniciado = (local: PremiumCtaLocal) => {
  gtag('event', 'checkout_iniciado', { local })
  gtag('event', 'premium_iniciado', { local })
  mirrorProduct('subscription_started', 'billing', { local })
}

export const trackCheckoutFalhou = (motivo: string) => {
  gtag('event', 'checkout_falhou', { motivo })
}

export const trackPremiumConcluido = () => {
  gtag('event', 'premium_concluido')
  mirrorProduct('subscription_completed', 'billing')
}

// ===========================================
// Cadastro / Login
// ===========================================

export const trackCadastroIniciado = () => {
  gtag('event', 'cadastro_iniciado')
  gtag('event', 'sign_up_iniciado')
}

export const trackCadastroConcluido = (metodo: 'email' | 'google' = 'email') => {
  gtag('event', 'cadastro_concluido', { metodo })
  gtag('event', 'sign_up_concluido', { metodo })
  mirrorProduct('signup', 'auth', { metodo })
}

export const trackLoginRealizado = (metodo: 'email' | 'google' = 'email') => {
  gtag('event', 'login_realizado', { metodo })
  mirrorProduct('login', 'auth', { metodo })
}

// ===========================================
// Blog
// ===========================================

export const trackBlogPostAberto = (slug: string, categoria: string) => {
  gtag('event', 'blog_post_aberto', { slug, categoria })
}

export const trackBlogCtaClicado = (slug: string, destino: string) => {
  gtag('event', 'blog_cta_clicado', { slug, destino })
}

export const trackBlogScrollProfundidade = (slug: string, profundidade: 25 | 50 | 75 | 100) => {
  gtag('event', 'blog_scroll_profundidade', { slug, profundidade })
}

export const trackBlogCategoriaFiltrada = (categoria: string) => {
  gtag('event', 'blog_categoria_filtrada', { categoria })
}

// ===========================================
// FAQ / Engajamento
// ===========================================

export const trackFaqPerguntaAberta = (pergunta: string) => {
  gtag('event', 'faq_pergunta_aberta', { pergunta })
}

export const trackFormularioContatoEnviado = () => {
  gtag('event', 'formulario_contato_enviado')
}

export const trackCookiesConsentimento = (aceito: boolean) => {
  gtag('event', 'cookies_consentimento', { aceito })
}

// ===========================================
// Templates imprimíveis
// ===========================================

export const trackTemplateAberto = (template: TemplateSlug) => {
  gtag('event', 'template_aberto', { template })
}

export const trackTemplateImpresso = (template: TemplateSlug) => {
  gtag('event', 'template_impresso', { template })
}

// ===========================================
// Conteúdo (descoberta — glossário, atividades, navegação)
// ===========================================

export const trackGlossarioTermoClicado = (termo: string) => {
  gtag('event', 'glossario_termo_clicado', { termo })
}

export const trackGlossarioTermoAberto = (termo: string) => {
  gtag('event', 'glossario_termo_aberto', { termo })
}

export const trackAtividadeMeiClicada = (atividade: string) => {
  gtag('event', 'atividade_mei_clicada', { atividade })
}

export const trackAtividadeMeiAberta = (atividade: string) => {
  gtag('event', 'atividade_mei_aberta', { atividade })
}

export const trackLinkInternoClicado = (secao: 'header' | 'footer', rotulo: string) => {
  gtag('event', 'link_interno_clicado', { secao, rotulo })
}

// ===========================================
// Indicação (referral viral loop)
// ===========================================

export const trackIndicacaoLinkCopiado = () => {
  gtag('event', 'indicacao_link_copiado')
}

export const trackIndicacaoLinkCompartilhado = (canal: 'whatsapp' | 'nativo' | 'fallback') => {
  gtag('event', 'indicacao_link_compartilhado', { canal })
}

// ===========================================
// Auth modal e fricção
// ===========================================

export const trackAuthModalAberto = (gatilho: string, tipo: 'login' | 'cadastro') => {
  gtag('event', 'auth_modal_aberto', { gatilho, tipo })
}

export const trackAuthModalFechado = () => {
  gtag('event', 'auth_modal_fechado')
}

export const trackGoogleOauthClicado = (tela: 'sign-in' | 'sign-up' | 'modal-login' | 'modal-signup') => {
  gtag('event', 'google_oauth_clicado', { tela })
}

export const trackSenhaRedefinicaoSolicitada = () => {
  gtag('event', 'senha_redefinicao_solicitada')
}

// ===========================================
// User Properties para segmentação
// ===========================================

export const setUserProperties = (props: {
  plano_usuario?: 'free' | 'ad_free'
  tipo_mei_usuario?: string
  total_calculos?: number
  total_faturamentos_registrados?: number
  meses_consecutivos?: number
  fez_indicacao?: boolean
  idade_conta_dias?: number
}) => {
  gtag('set', 'user_properties', props)
}

// ===========================================
// Window typings
// ===========================================

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void
  }
}
